import logger, { VERBOSE } from "./logger";
import {
  type DataSnapshot,
  type Unsubscribe,
  getAppInstance,
  getDatabase,
  onValue as onFirebaseValue,
  ref,
} from "../../dependencies/firebase";

export default async function subscribe<
  PathDefinition extends { onlyOnce?: boolean },
  Availability,
>(
  pathDefinition: Omit<PathDefinition, "onlyOnce">,
  unsubOne: (_path: string) => void,
  addUnsub: (_path: string, unsub: Unsubscribe) => void,
  definitionToPath: (def: Omit<PathDefinition, "onlyOnce">) => string,
  getAvailabilityFromSnapshot: (
    snapshot: DataSnapshot,
    path: string,
  ) => Availability | null,
  onValue: (
    value: Omit<PathDefinition, "onlyOnce"> & {
      availability: Availability | null;
    },
  ) => Promise<void>,
  abortSignal: AbortSignal,
  onlyOnce = false,
) {
  const path = definitionToPath(pathDefinition);

  if (VERBOSE) {
    logger.log(
      `${onlyOnce ? "Fetching once" : "Subscribing to"}: "${path}" ...`,
    );
  }

  await new Promise<void>((resolve, reject) => {
    let settled = false;

    const settleResolve = () => {
      if (settled) {
        return;
      }
      settled = true;
      abortSignal.removeEventListener("abort", unsubOnAbort);
      resolve();
    };

    const settleReject = (error: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      abortSignal.removeEventListener("abort", unsubOnAbort);
      reject(error);
    };

    function unsubOnAbort() {
      if (VERBOSE) {
        logger.log(`Unsub on abort: "${path}"`);
      }

      unsubOne(path);
      settleResolve();
    }

    async function onSuccess(snapshot: DataSnapshot) {
      if (VERBOSE) {
        logger.log(`On success: "${path}"`);
      }

      if (!onlyOnce) {
        if (VERBOSE) {
          logger.log(`On success - remove abort listener: "${path}"`);
        }
        abortSignal.removeEventListener("abort", unsubOnAbort);
      }

      if (abortSignal.aborted) {
        if (VERBOSE) {
          logger.log(`On success aborted early: "${path}"`);
        }
        settleResolve();
        return;
      }

      const availability = getAvailabilityFromSnapshot(snapshot, path);

      await onValue({
        ...pathDefinition,
        availability,
      });

      settleResolve();
    }

    function onError(error: Error) {
      if (VERBOSE) {
        logger.log(`On error: "${path}"`);
      }

      if (!onlyOnce) {
        if (VERBOSE) {
          logger.log(`On error - remove abort listener: "${path}"`);
        }
        abortSignal.removeEventListener("abort", unsubOnAbort);
      }

      settleReject(error);
    }

    const firebaseRef = ref(getDatabase(getAppInstance()), path);
    const firebaseUnsub = onFirebaseValue(firebaseRef, onSuccess, onError, {
      onlyOnce,
    });

    if (!onlyOnce) {
      const wrappedUnsub = () => {
        settleResolve();
        firebaseUnsub();
      };

      addUnsub(path, wrappedUnsub);

      abortSignal.addEventListener("abort", unsubOnAbort, {
        once: true,
      });
    }
  });

  if (VERBOSE) {
    logger.log(
      `${onlyOnce ? "Fetching once" : "Subscribing to"}: "${path}" done`,
    );
  }
}
